/**
 * Theme management for light / dark mode.
 *
 * The `dark` class on <html> is the single mechanism Tailwind looks at;
 * everything in this module is coordination around getting that class
 * to match the user's preference. localStorage persists explicit
 * choices ("light" / "dark"); falling back to "system" defers to
 * `prefers-color-scheme` so an unconfigured user gets whatever their
 * OS is set to.
 *
 * `useTheme` is a React hook that re-renders consumers when the theme
 * changes — used by the header toggle to flip its icon. The hook
 * doesn't drive the DOM update; the imperative `applyTheme` does, so
 * the boot-time call in main.tsx happens before React mounts and we
 * avoid a flash of incorrect theme.
 */

import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'partforge.theme';

function readStoredTheme(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* localStorage unavailable */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function resolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'light' || theme === 'dark') return theme;
  return systemPrefersDark() ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  const effective = resolvedTheme(theme);
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

// Pub/sub so React components re-render when the theme changes. Cheap —
// the only listener is the toggle's icon — but keeps the hook honest.
const listeners = new Set<() => void>();
function notify(): void {
  for (const listener of listeners) listener();
}

let current: Theme = readStoredTheme();

export function setTheme(next: Theme): void {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* persisting failed; the in-memory value still wins for this session */
  }
  applyTheme(next);
  notify();
}

export function getTheme(): Theme {
  return current;
}

// Boot-time hook called from main.tsx so the right class is on <html>
// before React renders. Also subscribes to system-level changes so a
// user with theme=system who flips their OS theme gets the update
// without a refresh.
export function initTheme(): void {
  applyTheme(current);
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', () => {
      if (current === 'system') {
        applyTheme(current);
        notify();
      }
    });
  }
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => current,
  );
}
